import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Calendar, Flame } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { articlesAPI } from '../utils/api';
import MobileCarousel from './MobileCarousel';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const AdviceCard = ({ a, cat, isTrending }) => (
  <Link
    to={`/blog/${a.slug}`}
    data-testid={`home-advice-card-${a.slug}`}
    className="group block h-full"
  >
    <Card className={`h-full overflow-hidden hover:shadow-lg transition-all border-stone-200 relative ${isTrending ? 'ring-2 ring-orange-400 border-orange-300' : 'hover:border-brand-300'}`}>
      {isTrending && (
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-label font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg"
          data-testid={`home-advice-trending-${a.slug}`}
        >
          <Flame className="h-3 w-3" />
          Tendance
        </div>
      )}
      {a.hero_image ? (
        <div className="h-44 overflow-hidden bg-stone-100">
          <img
            src={a.hero_image}
            alt={a.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      ) : (
        <div className="h-44 bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
          <BookOpen className="h-10 w-10 text-orange-300" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {cat && (
            <Badge className="bg-orange-100 text-orange-800 text-label font-semibold">
              {cat.label}
            </Badge>
          )}
          {a.published_at && (
            <span className="text-xs text-stone-500 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(a.published_at)}
            </span>
          )}
        </div>
        <h3 className="font-bold text-base text-stone-900 mb-2 line-clamp-2 group-hover:text-brand-600 transition-colors">
          {a.title}
        </h3>
        {a.excerpt && (
          <p className="text-sm text-stone-600 line-clamp-3">{a.excerpt}</p>
        )}
        <div className="flex items-center text-brand-600 text-sm font-semibold mt-3">
          Lire l&apos;article
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Card>
  </Link>
);

/**
 * Homepage "Conseils immobiliers" — 3 most recent articles; the most viewed
 * one over 7 days gets a "Tendance" badge. Carousel on phones.
 */
const HomeAdviceSection = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trendingSlug, setTrendingSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      articlesAPI.listPublic({ limit: 3 }).catch(() => ({ data: { items: [] } })),
      articlesAPI.listCategories().catch(() => ({ data: { categories: [] } })),
      articlesAPI.getTrendingWeekly().catch(() => ({ data: { article: null } })),
    ]).then(([articlesRes, catRes, trendingRes]) => {
      if (!alive) return;
      const list = articlesRes.data?.items || articlesRes.data || [];
      setItems(Array.isArray(list) ? list.slice(0, 3) : []);
      setCategories(catRes.data?.categories || []);
      setTrendingSlug(trendingRes.data?.article?.slug || null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  if (!loading && items.length === 0) return null;

  const cards = items.map((a) => (
    <AdviceCard
      key={a.id || a.slug}
      a={a}
      cat={categories.find((c) => c.id === a.category)}
      isTrending={Boolean(trendingSlug && a.slug === trendingSlug)}
    />
  ));

  return (
    <section
      id="news"
      className="mb-20 scroll-mt-24"
      data-testid="home-advice-section"
    >
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 mb-2">
            <BookOpen className="h-3.5 w-3.5" />
            Conseils immobiliers
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
            Réussissez votre projet immobilier
          </h2>
          <p className="text-stone-500 text-sm sm:text-base mt-2">
            Guides, analyses du marché camerounais et conseils d&apos;experts — mis à jour régulièrement.
          </p>
        </div>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          data-testid="home-advice-view-all"
        >
          Voir tous les conseils
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-advice-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-stone-100 rounded-xl h-72 animate-pulse" />
          ))}
        </div>
      ) : (
        <MobileCarousel
          testId="home-advice"
          slides={cards}
          desktop={<div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-advice-grid">{cards}</div>}
        />
      )}
    </section>
  );
};

export default HomeAdviceSection;
